import React, { useEffect, useMemo, useState } from "react";
import { Table, Select, Button, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getFacebookLeadForms,
  getFacebookFormLeads,
} from "../features/customers/customerSlice";
import "../styles/leads.css";
import { useMediaQuery } from "react-responsive";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const lostStatuses = [
  "bought_elsewhere",
  "not_interested",
  "no_budget",
  "no_response",
  "spam",
];

const statusOptions = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "follow_up", label: "Follow Up" },
  { value: "appointment_booked", label: "Appointment Booked" },
  { value: "deal_closed", label: "Deal Closed" },
  { value: "bought_elsewhere", label: "Lost - Bought Elsewhere" },
  { value: "not_interested", label: "Lost - Not Interested" },
  { value: "no_budget", label: "Lost - No Budget" },
  { value: "no_response", label: "Lost - No Response" },
  { value: "spam", label: "Lost - Spam" },
];

const statusLabelMap = {
  new: "New",
  contacted: "Contacted",
  follow_up: "Follow Up",
  appointment_booked: "Appointment Booked",
  deal_closed: "Deal Closed",
  bought_elsewhere: "Lost - Bought Elsewhere",
  not_interested: "Lost - Not Interested",
  no_budget: "Lost - No Budget",
  no_response: "Lost - No Response",
  spam: "Lost - Spam",
};

const FacebookAgentsLeads = ({ setActiveLeadView }) => {
  const dispatch = useDispatch();
  const [selectedFormId, setSelectedFormId] = useState("all");
  const [leadStatuses, setLeadStatuses] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [allFormsLeads, setAllFormsLeads] = useState([]);
  const [loadingAllForms, setLoadingAllForms] = useState(false);
  const [hasFetchedForms, setHasFetchedForms] = useState(false);
  const [hasFetchedLeads, setHasFetchedLeads] = useState(false);

  const isDesktop = useMediaQuery({ minWidth: 785 });

  const {
    forms = [],
    formLeads = [],
    isLoading,
  } = useSelector((state) => state.customer);

  useEffect(() => {
    if (Array.isArray(forms) && forms.length > 0) {
      setHasFetchedForms(true);
      return;
    }

    const fetchForms = async () => {
      try {
        await dispatch(getFacebookLeadForms()).unwrap();
      } catch (error) {
        console.error("Failed to fetch Facebook lead forms:", error);
      } finally {
        setHasFetchedForms(true);
      }
    };

    fetchForms();
  }, [dispatch, forms]);

  const activeForms = useMemo(() => {
    return forms.filter((form) => form.status === "ACTIVE");
  }, [forms]);

  const fetchAllActiveFormsLeads = async () => {
    try {
      setLoadingAllForms(true);

      const results = await Promise.all(
        activeForms.map((form) =>
          dispatch(getFacebookFormLeads(form.id)).unwrap()
        )
      );

      const combinedLeads = results.flatMap((result) => result.leads || []);
      setAllFormsLeads(combinedLeads);
    } catch (error) {
      console.error("Failed to fetch all active form leads:", error);
      setAllFormsLeads([]);
    } finally {
      setLoadingAllForms(false);
      setHasFetchedLeads(true);
    }
  };

  useEffect(() => {
    if (!hasFetchedForms) return;

    if (selectedFormId === "all") {
      if (allFormsLeads.length > 0) {
        setHasFetchedLeads(true);
        return;
      }

      if (activeForms.length > 0) {
        fetchAllActiveFormsLeads();
      } else {
        setHasFetchedLeads(true);
      }

      return;
    }

    if (selectedFormId !== "all" && Array.isArray(formLeads) && formLeads.length > 0) {
      setHasFetchedLeads(true);
    }
  }, [hasFetchedForms, selectedFormId, allFormsLeads, activeForms, formLeads]);

  useEffect(() => {
    const currentLeads = selectedFormId === "all" ? allFormsLeads : formLeads;

    if (currentLeads.length > 0) {
      setLeadStatuses((prev) => {
        const nextStatuses = { ...prev };

        currentLeads.forEach((lead) => {
          if (!nextStatuses[lead.leadgenId]) {
            nextStatuses[lead.leadgenId] = "new";
          }
        });

        return nextStatuses;
      });
    }
  }, [allFormsLeads, formLeads, selectedFormId]);

  const handleFormChange = async (value) => {
    setSelectedFormId(value);
    setSelectedMonth("all");
    setSelectedWeek("all");
    setHasFetchedLeads(false);

    if (value === "all") {
      if (allFormsLeads.length > 0) {
        setHasFetchedLeads(true);
        return;
      }

      await fetchAllActiveFormsLeads();
    } else {
      try {
        await dispatch(getFacebookFormLeads(value)).unwrap();
      } catch (error) {
        console.error("Failed to fetch selected form leads:", error);
      } finally {
        setHasFetchedLeads(true);
      }
    }
  };

  const handleStatusChange = (leadgenId, value) => {
    setLeadStatuses((prev) => ({
      ...prev,
      [leadgenId]: value,
    }));
  };

  const truncateNameToTwoWords = (name) => {
    if (!name || typeof name !== "string") return "Not provided";

    const cleaned = name.replace(/\.+/g, " ").trim();
    const words = cleaned.split(/\s+/).filter(Boolean);

    if (words.length <= 2) return cleaned;

    return `${words.slice(0, 2).join(" ")}...`;
  };

  const getWeekOfMonth = (dateString) => {
    const date = new Date(dateString);
    return Math.ceil(date.getDate() / 7);
  };

  const monthOptions = [
    { value: "all", label: "All Months" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const weekOptions = [
    { value: "all", label: "All Weeks" },
    { value: 1, label: "Week 1" },
    { value: 2, label: "Week 2" },
    { value: 3, label: "Week 3" },
    { value: 4, label: "Week 4" },
    { value: 5, label: "Week 5" },
  ];

  const selectedLeads = useMemo(() => {
    return selectedFormId === "all" ? allFormsLeads : formLeads;
  }, [selectedFormId, allFormsLeads, formLeads]);

  const filteredLeads = useMemo(() => {
    return selectedLeads.filter((lead) => {
      const createdAt = lead.createdTime ? new Date(lead.createdTime) : null;
      if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

      const leadMonth = createdAt.getMonth();
      const leadWeek = getWeekOfMonth(lead.createdTime);

      let matchesMonth = true;
      let matchesWeek = true;

      if (selectedMonth !== "all") {
        matchesMonth = leadMonth === selectedMonth;
      }

      if (selectedWeek !== "all") {
        matchesWeek = leadWeek === selectedWeek;
      }

      return matchesMonth && matchesWeek;
    });
  }, [selectedLeads, selectedMonth, selectedWeek]);

  const totalLeads = filteredLeads.length;
  const contacted = filteredLeads.filter(
    (lead) => (leadStatuses[lead.leadgenId] || "new") !== "new"
  ).length;
  const followUps = filteredLeads.filter(
    (lead) => (leadStatuses[lead.leadgenId] || "new") === "follow_up"
  ).length;
  const appointmentBooked = filteredLeads.filter(
    (lead) => (leadStatuses[lead.leadgenId] || "new") === "appointment_booked"
  ).length;
  const dealsClosed = filteredLeads.filter(
    (lead) => (leadStatuses[lead.leadgenId] || "new") === "deal_closed"
  ).length;
  const dealsLost = filteredLeads.filter((lead) =>
    lostStatuses.includes(leadStatuses[lead.leadgenId] || "new")
  ).length;

  const dataSource = useMemo(() => {
    return filteredLeads.map((lead, index) => ({
      key: lead.leadgenId || index + 1,
      leadgenId: lead.leadgenId || "",
      sno: index + 1,
      name: truncateNameToTwoWords(lead.name),
      fullName: lead.name || "Not provided",
      phone: lead.phone || "Not provided",
      assignedTo: "Team",
      status: leadStatuses[lead.leadgenId] || "new",
      date: lead.createdTime
        ? new Date(lead.createdTime).toLocaleDateString()
        : "Not available",
      rawLead: lead,
    }));
  }, [filteredLeads, leadStatuses]);

  const renderStatusSelect = (record, fullWidth = false) => (
    <Select
      size="small"
      value={record.status}
      className={fullWidth ? "lead-status-select w-100" : "lead-status-select"}
      style={{ width: "100%" }}
      onChange={(value) => handleStatusChange(record.leadgenId, value)}
      options={statusOptions}
    />
  );

  const columns = [
    { title: "SNo", dataIndex: "sno", width: 70 },
    {
      title: "Name",
      dataIndex: "name",
      render: (_, record) => <span title={record.fullName}>{record.name}</span>,
    },
    { title: "Phone", dataIndex: "phone" },
    { title: "Assigned To", dataIndex: "assignedTo" },
    {
      title: "Status",
      render: (_, record) => (
        <Select
          size="small"
          value={record.status}
          style={{ width: 180 }}
          onChange={(value) => handleStatusChange(record.leadgenId, value)}
          options={statusOptions}
        />
      ),
    },
    { title: "Date", dataIndex: "date" },
  ];

  const formOptions = [
    { value: "all", label: "All Lead Forms" },
    ...activeForms.map((form) => ({
      value: form.id,
      label: form.name,
    })),
  ];

  const loadingPage =
    !hasFetchedForms ||
    !hasFetchedLeads ||
    loadingAllForms ||
    (isLoading &&
      ((selectedFormId === "all" && allFormsLeads.length === 0) ||
        (selectedFormId !== "all" && formLeads.length === 0)));

  const renderKpiSkeletons = () => (
    <div className="leads__count-wrapper g-3 mb-4">
      {Array.from({ length: 6 }).map((_, index) => (
        <div className="col-xl-3 col-lg-3 col-md-6 col-12" key={index}>
          <div className="bg-white p-3 rounded-3 shadow-sm">
            <Skeleton height={12} width="50%" style={{ marginBottom: 10 }} />
            <Skeleton height={22} width="40%" />
          </div>
        </div>
      ))}
    </div>
  );

  const renderDesktopTableSkeleton = () => (
    <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: "grid",
            gridTemplateColumns: "60px 1.2fr 1.2fr 1fr 1.2fr 120px",
            gap: 12,
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Skeleton height={12} />
          <Skeleton height={12} />
          <Skeleton height={12} />
          <Skeleton height={12} />
          <Skeleton height={12} />
          <Skeleton height={12} />
        </div>
      ))}
    </div>
  );

  const renderMobileCardsSkeleton = () => (
    <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
      <div className="d-flex flex-column gap-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="lead-mobile-card border rounded-4 p-3 shadow-sm bg-white"
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Skeleton width={80} height={12} />
              <Skeleton width={90} height={18} />
            </div>

            <div className="mb-3">
              <Skeleton width="60%" height={14} style={{ marginBottom: 6 }} />
              <Skeleton width="50%" height={12} />
            </div>

            <div className="mb-3">
              <Skeleton width={80} height={10} style={{ marginBottom: 6 }} />
              <Skeleton width="100%" height={12} />
            </div>

            <div className="mb-3">
              <Skeleton width={70} height={10} style={{ marginBottom: 6 }} />
              <Skeleton width="100%" height={30} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="facebook__leads-wrapper">
      {isDesktop && (
        <div className=" mb-3">
          <h3 className="mb-1">Facebook Leads</h3>
        </div>
      )}

      <div className="facebook__leads-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div className="leads-nav">
          <Button
            className="leads-nav__item leads-nav__item--whatsapp"
            onClick={() => setActiveLeadView("whatsapp")}
          >
            WhatsApp Leads
          </Button>

          <Button
            className="leads-nav__item leads-nav__item--facebook leads-nav__item--active"
            onClick={() => setActiveLeadView("facebook")}
          >
            Facebook Leads
          </Button>
        </div>

        <div className="facebook__sort-wrapper">
          <Select
            value={selectedMonth}
            onChange={(value) => {
              setSelectedMonth(value);
              setSelectedWeek("all");
            }}
            options={monthOptions}
          />

          <Select
            value={selectedWeek}
            onChange={(value) => setSelectedWeek(value)}
            options={weekOptions}
          />

          <Select
            placeholder="Select Facebook Lead Form"
            value={selectedFormId || undefined}
            onChange={handleFormChange}
            options={formOptions}
          />
        </div>
      </div>

      {loadingPage ? (
        renderKpiSkeletons()
      ) : (
        <div className="leads__count-wrapper g-3 mb-4">
          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #4f46e5" }}
            >
              <small style={{ color: "#4f46e5" }}>Total Leads</small>
              <h4 className="mb-0" style={{ color: "#1e1b4b" }}>
                {totalLeads}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #0891b2" }}
            >
              <small style={{ color: "#0891b2" }}>Contacted</small>
              <h4 className="mb-0" style={{ color: "#164e63" }}>
                {contacted}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #ea580c" }}
            >
              <small style={{ color: "#ea580c" }}>Follow Ups</small>
              <h4 className="mb-0" style={{ color: "#7c2d12" }}>
                {followUps}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #7c3aed" }}
            >
              <small style={{ color: "#7c3aed" }}>Appointments</small>
              <h4 className="mb-0" style={{ color: "#4c1d95" }}>
                {appointmentBooked}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #16a34a" }}
            >
              <small style={{ color: "#16a34a" }}>Deals Closed</small>
              <h4 className="mb-0" style={{ color: "#14532d" }}>
                {dealsClosed}
              </h4>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-12">
            <div
              className="bg-white p-3 rounded-3 shadow-sm"
              style={{ borderLeft: "5px solid #dc2626" }}
            >
              <small style={{ color: "#dc2626" }}>Deals Lost</small>
              <h4 className="mb-0" style={{ color: "#7f1d1d" }}>
                {dealsLost}
              </h4>
            </div>
          </div>
        </div>
      )}

      {loadingPage ? (
        isDesktop ? renderDesktopTableSkeleton() : renderMobileCardsSkeleton()
      ) : (
        <div className="bg-white p-3 p-md-4 leads-main-wrapper rounded-3">
          {isDesktop ? (
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 900 }}
            />
          ) : (
            <div className="d-flex flex-column gap-3">
              {dataSource.length > 0 ? (
                dataSource.map((record) => (
                  <div
                    key={record.key}
                    className="lead-mobile-card border rounded-4 p-3 shadow-sm bg-white"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <small className="text-muted">{record.date}</small>
                      <Tag color="blue" className="mb-0">
                        {statusLabelMap[record.status]}
                      </Tag>
                    </div>

                    <div className="d-flex flex-column mb-3">
                      <h6 className="mb-1 fw-semibold">{record.fullName}</h6>

                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">Phone:</small>
                        <span>{record.phone || "Not provided"}</span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <small className="text-muted d-block mb-1">Assigned To</small>
                      <div>{record.assignedTo}</div>
                    </div>

                    <div className="mb-3">
                      <small className="text-muted d-block mb-1">Status</small>
                      {renderStatusSelect(record, true)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted">No leads found</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FacebookAgentsLeads;